/**
* 分析看板设置弹框组件
*
* @author By AICode
* @date 2026-04-02
*/
<template>
  <a-modal title="分析看板设置" :visible="visible" :mask-closable="false" ok-text="确定" cancel-text="取消"
    :wrapClassName="'ops-dashboard-setting-modal'" @ok="handleOk" @cancel="handleCancel">
    <div class="ops-dashboard-setting">
      <div class="ops-dashboard-table">
        <div class="ops-dashboard-thead">
          <div class="ops-dashboard-th ops-dashboard-col-order">顺序</div>
          <div class="ops-dashboard-th ops-dashboard-col-name">看板名称</div>
          <div class="ops-dashboard-th ops-dashboard-col-action">操作</div>
        </div>
        <draggable v-model="localList" handle=".ops-dashboard-drag-handle" animation="200" class="ops-dashboard-tbody"
          ghost-class="ops-dashboard-drag-ghost" @end="handleDragEnd">
          <div v-for="row in localList" :key="row.id" class="ops-dashboard-tr">
            <div class="ops-dashboard-td ops-dashboard-col-order">
              <span class="ops-dashboard-drag-handle">
                <a-icon type="menu" />
              </span>
            </div>
            <div class="ops-dashboard-td ops-dashboard-col-name">
              <template v-if="editingDashboardId === row.id">
                <a-input v-model="editingDashboardName" size="small" class="ops-dashboard-name-input" :maxLength=20
                  @pressEnter="handleDashboardRenameCommit" @keyup.esc.native="handleDashboardRenameCancel" />
                <a-icon type="check" class="ops-dashboard-confirm-icon" @click="handleDashboardRenameCommit" />
                <a-icon type="close" class="ops-dashboard-cancel-icon" @click="handleDashboardRenameCancel" />
              </template>
              <template v-else>
                <span class="ops-dashboard-name-text">{{ row.sheetName }}</span>
                <a-icon v-if="row.isSystem !== 1" type="edit" class="ops-dashboard-edit-icon"
                  @click="handleDashboardRenameStart(row)" />
              </template>
            </div>
            <div class="ops-dashboard-td ops-dashboard-col-action">
              <span v-if="row.isSystem === 1" class="ops-dashboard-action-disabled">--</span>

              <a v-else class="ops-dashboard-delete">
                <a-popconfirm :title="`确认删除吗？`" ok-text="确认" cancel-text="取消" @confirm="handleDashboardDelete(row.id)">
                  删除
                </a-popconfirm>
              </a>
            </div>
          </div>
        </draggable>
      </div>
    </div>
  </a-modal>
</template>

<script>
import './DashboardSetting.less'

import { Modal, Icon, Input,Popconfirm } from 'ant-design-vue'
import draggable from 'vuedraggable'

export default {
  name: 'DashboardSetting',
  components: {
    AModal: Modal,
    AIcon: Icon,
    AInput: Input,
    'a-popconfirm': Popconfirm,
    draggable,
  },
  props: {
    visible: { type: Boolean, default: false },
    list: { type: Array, default: () => [] },
  },
  data() {
    return {
      localList: [],
      editingDashboardId: '',
      editingDashboardName: '',
      editingDashboardOriginalName: '',
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.localList = this.list.map((item) => ({ ...item }))
        this.editingDashboardId = ''
        this.editingDashboardName = ''
        this.editingDashboardOriginalName = ''
      }
    },
    list: {
      handler(val) {
        if (!this.visible) {
          this.localList = val.map((item) => ({ ...item }))
        }
      },
      deep: true,
    },
  },
  methods: {
    // 点击确定前先提交正在进行的编辑，保证 localList 最终状态正确 - By AICode
    handleOk() {
      this.handleDashboardRenameCommit()
      this.$emit('ok', this.localList)
    },
    // 取消时重置编辑状态 - By AICode
    handleCancel() {
      this.editingDashboardId = ''
      this.editingDashboardName = ''
      this.editingDashboardOriginalName = ''
      this.$emit('cancel')
    },
    // 拖拽结束时若仍有编辑态，聚焦回输入框保持连续编辑体验 - By AICode
    handleDragEnd() {
      if (this.editingDashboardId) {
        this.$nextTick(() => {
          const input = this.$el && this.$el.querySelector('.ops-dashboard-name-input input')
          if (input) input.focus()
        })
      }
    },
    // 守卫与模板保持一致，isSystem === 1 的系统看板不允许删除 - By AICode
    handleDashboardDelete(id) {
      const idx = this.localList.findIndex((item) => item.id === id)
      if (idx < 0) return
      if (this.localList[idx].isSystem === 1) return
      this.localList.splice(idx, 1)
      if (this.editingDashboardId === id) {
        this.editingDashboardId = ''
        this.editingDashboardName = ''
        this.editingDashboardOriginalName = ''
      }
    },
    // 进入编辑态并聚焦输入框，记录原始名称用于 Escape 回退 - By AICode
    handleDashboardRenameStart(row) {
      this.editingDashboardId = row.id
      this.editingDashboardName = row.sheetName
      this.editingDashboardOriginalName = row.sheetName
      this.$nextTick(() => {
        const input = this.$el && this.$el.querySelector('.ops-dashboard-name-input input')
        if (input) input.focus()
      })
    },
    // 提交编辑：名称为空时回退到原名称，保证 localList 始终有有效值 - By AICode
    handleDashboardRenameCommit() {
      if (!this.editingDashboardId) return
      const name = (this.editingDashboardName || '').trim()
      const idx = this.localList.findIndex((item) => item.id === this.editingDashboardId)
      if (idx > -1) {
        const finalName = name || this.editingDashboardOriginalName
        this.$set(this.localList, idx, { ...this.localList[idx], sheetName: finalName })
      }
      this.editingDashboardId = ''
      this.editingDashboardName = ''
      this.editingDashboardOriginalName = ''
    },
    // Escape 取消编辑，localList 不变 - By AICode
    handleDashboardRenameCancel() {
      this.editingDashboardId = ''
      this.editingDashboardName = ''
      this.editingDashboardOriginalName = ''
    },
  },
}
</script>
